import { Box, ListItem, ListItemIcon, ListItemText, Typography } from "@mui/material";

import BaseEntitySearchAutocomplete, {
  BaseEntitySearchAutocompleteInheritableProps,
} from "@/common/components/Entity/components/BaseEntitySearchAutocomplete";
import AppIcon from "@/common/components/Icons/AppIcon";
import { renderIf } from "@/common/helpers/render/renderIf";
import { StringHelper } from "@/common/helpers/string";
import { enumService } from "@/common/services/enum";
import { AutocompleteOptionType, BaseAutocompleteOption } from "@/common/ts/autocomplete";
import { apiClient } from "@/core/api/ApiClient";
import { SupplierDto, SupplierGetPaginatedDto } from "@/core/api/generated/v1";

import InlineApiEnumValueList from "../../Enum/InlineApiEnumValueList";

export type SupplierAutocompleteOption = BaseAutocompleteOption<SupplierDto>;

export const getSupplierOptionTitle = (data: SupplierDto): string =>
  StringHelper.join(
    [
      data.name || data.contact?.personName?.name,
      ...(data?.types
        ? data.types.map((type) => enumService.getEnumValueName("SupplierType", type))
        : []),
    ],
    ", ",
    {
      skipEmpty: true,
    },
  );

export const supplierToOption = (data: SupplierDto): SupplierAutocompleteOption => ({
  id: data.id!,
  title: getSupplierOptionTitle(data),
  optionType: AutocompleteOptionType.Normal,
  data: data,
});

export interface SupplierAutocompleteProps extends BaseEntitySearchAutocompleteInheritableProps<SupplierDto> {
  searchFilters?: Partial<Omit<SupplierGetPaginatedDto, "search" | "includeIds">>;
}

export default function SupplierAutocomplete({
  searchFilters,

  isPreload = true,
  ...otherProps
}: SupplierAutocompleteProps) {
  return (
    <BaseEntitySearchAutocomplete
      sx={{ minWidth: 200, flex: 1 }}
      entityToOption={supplierToOption}
      isPreload={isPreload}
      request={{
        requestFunc: apiClient.suppliersApi.apiV1SuppliersGetPost,
        limit: 25,
        parameters: {
          nexusOpsTenant: EMPTY_TENANT_IDENTIFIER,
          supplierGetPaginatedDto: {
            ...searchFilters,
          },
        },
        combineParameters: (params, newParams) => ({
          ...params,
          supplierGetPaginatedDto: {
            ...params.supplierGetPaginatedDto,
            ...newParams,
          },
        }),
        deps: [searchFilters],
      }}
      label='Supplier'
      placeholder='Search for supplier...'
      renderOption={(props, option) => {
        return (
          <ListItem {...props}>
            <ListItemIcon>
              {renderIf()
                .if(option.optionType === AutocompleteOptionType.Normal)
                .then(<AppIcon of='supplier' />)
                .elseif(option.optionType === AutocompleteOptionType.DynamicCreateNew)
                .then(<AppIcon of='add' />)
                .render()}
            </ListItemIcon>
            <ListItemText
              primary={
                <>
                  <Typography
                    variant={
                      option.optionType === AutocompleteOptionType.DynamicCreateNew
                        ? "subtitle1"
                        : "body1"
                    }
                    component='div'
                  >
                    {option.title}
                  </Typography>
                  {option.data && (
                    <Typography component='div' variant='body2'>
                      <Box>{option.data?.contact?.email}</Box>
                      <Box>
                        <InlineApiEnumValueList type='SupplierType' values={option.data?.types} />
                      </Box>
                    </Typography>
                  )}
                </>
              }
            />
          </ListItem>
        );
      }}
      {...otherProps}
    />
  );
}
